import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listTheories } from "@/api";
import { AX_HOME as HOME } from "@/constants/testIds";
import { useAuth } from "@/auth";
import CredibilityBar from "@/components/CredibilityBar";
import AsciiDivider from "@/components/AsciiDivider";
import FeaturedDossier, { pickFeatured } from "@/components/FeaturedDossier";
import LiveIntelFeed from "@/components/LiveIntelFeed";

const TAGLINES = [
  "EVIDENCE OVER NARRATIVE.",
  "PARALLEL ACCOUNTS. UNFLINCHING.",
  "WHAT THEY ARGUED. WHAT THEY HID.",
  "DOSSIER ACCESS: PARTIAL.",
];

const Home = () => {
  const [theories, setTheories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [tagIdx] = useState(() => Math.floor(Math.random() * TAGLINES.length));

  useEffect(() => {
    (async () => {
      try {
        const data = await listTheories();
        setTheories(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const remaining =
    user && user.subscription_status !== "active"
      ? Math.max(0, (user.free_view_limit || 2) - (user.views_used || 0))
      : null;

  return (
    <div className="term-container fade-in" data-testid={HOME.root}>
      <header style={{ marginBottom: "2rem" }}>
        <div className="kbd-label" style={{ marginBottom: "0.4rem" }}>
          // AXIOM ARCHIVE
        </div>
        <h1
          className="glow"
          style={{
            margin: 0,
            fontSize: "clamp(2rem, 5vw, 3rem)",
            letterSpacing: "0.18em",
            fontWeight: 700,
          }}
        >
          AXIOM<span className="cursor" />
        </h1>
        <p style={{ color: "var(--ax-text-dim)", maxWidth: 620, marginTop: "0.6rem" }}>
          {TAGLINES[tagIdx]} A dossier of contested events, drawn from
          declassified records, court testimony, and public reporting. You
          decide which version survives contact with the evidence.
        </p>

        {user && (
          <div className="muted" style={{ fontSize: 12, marginTop: "0.6rem" }}>
            session: <span className="glow-dim">{user.codename}</span>
            {user.subscription_status === "active" ? (
              <span className="pill active" style={{ marginLeft: 8 }}>
                FULL ACCESS
              </span>
            ) : (
              <span
                className="pill warn"
                style={{ marginLeft: 8 }}
                title="Free dossier views remaining"
              >
                {remaining} FREE VIEW{remaining === 1 ? "" : "S"} REMAINING
              </span>
            )}
          </div>
        )}
      </header>

      <AsciiDivider />

      {!loading && theories.length > 0 && (
        <FeaturedDossier theories={theories} />
      )}

      <LiveIntelFeed />

      <div className="kbd-label" style={{ margin: "1.5rem 0 0.6rem" }}>
        $ ls -la dossiers/
      </div>

      {loading && <p className="muted">Loading dossiers<span className="cursor" /></p>}

      <ul
        data-testid={HOME.theoryList}
        style={{ listStyle: "none", padding: 0, margin: "1rem 0 3rem" }}
      >
        {(() => {
          const featured = !loading ? pickFeatured(theories) : null;
          const rest = featured
            ? theories.filter((t) => t.slug !== featured.slug)
            : theories;
          return rest;
        })().map((t) => {
          const cred =
            t.community_credibility != null ? t.community_credibility : t.base_credibility;
          return (
            <li
              key={t.slug}
              data-testid={HOME.theoryItem(t.slug)}
              className="term-card"
            >
              <Link
                to={`/theories/${t.slug}`}
                data-testid={HOME.theoryLink(t.slug)}
                style={{
                  color: "var(--ax-text)",
                  display: "block",
                  borderBottom: "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div className="kbd-label">// dossier</div>
                    <div
                      style={{
                        fontSize: "1.15rem",
                        fontWeight: 700,
                        color: "var(--ax-accent)",
                        letterSpacing: "0.05em",
                        marginTop: 4,
                      }}
                    >
                      {t.title}
                    </div>
                  </div>
                  <span className="muted" style={{ fontSize: 11 }}>
                    {t.community_vote_count} community votes
                  </span>
                </div>
                <div style={{ marginTop: "0.6rem" }}>
                  <CredibilityBar
                    score={cred}
                    label={
                      t.community_credibility != null ? "COMMUNITY" : "BASELINE"
                    }
                  />
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      <AsciiDivider />
      <p className="faint" style={{ marginTop: "1.5rem", fontSize: 12 }}>
        Each session is anonymous. To preserve your codename across devices,
        visit <Link to="/account">/account</Link> and set a passphrase. To
        restore an existing codename, visit{" "}
        <Link to="/recover">/recover</Link>.
      </p>
    </div>
  );
};

export default Home;
